import React from "react";

import PropTypes from "prop-types";

import { ATTACHMENT_TYPES } from "manager/config/constants";
import Settings from "shared/components/Settings";
import Skeleton from "shared/components/Skeleton";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import ApplicationSetupHeader from "../Header";
import { useAttachmentValidation } from "../DealSetupForm/AttachmentValidationContext";
import { ConsolidatedAttachmentGrid } from "../ConsolidatedAttachmentGrid";
import { IncomeSurfaceGate } from "../IncomeSurfaceGate";
import SupportedCountriesSection from "./SupportedCountriesSection";
import {
  buildApplicantTypes,
  buildItemDisplayLabel,
  getAttachmentsFilter,
  mapIntlDocLabels,
  useAttachmentHandlers,
  useDocQuantitiesUiEnabled,
  useGuardedIncomeDocsToggle,
} from "../utils";
import RequiredAttachmentsInternationalDocumentsLegacy from "./RequiredAttachmentsInternationalDocumentsLegacy";

const APPLICANT_TYPES_CONFIG = buildApplicantTypes({
  applicant: "requiresApplicantInternationalDocuments",
  occupant: "requiresOccupantInternationalDocuments",
  guarantor: "requiresGuarantorInternationalDocuments",
});

const ATTACHMENT_TYPE = ATTACHMENT_TYPES.INTERNATIONAL_DOCUMENTS;
const getRequiredAttachments = getAttachmentsFilter(ATTACHMENT_TYPE);

const RequiredAttachmentsInternationalDocuments = ({ loading, disabled, forSettingsLabel }) => {
  const uiEnabled = useDocQuantitiesUiEnabled();
  if (!uiEnabled) {
    return (
      <RequiredAttachmentsInternationalDocumentsLegacy
        loading={loading}
        disabled={disabled}
        forSettingsLabel={forSettingsLabel}
      />
    );
  }

  return (
    <RequiredAttachmentsInternationalDocumentsGrid
      loading={loading}
      disabled={disabled}
    />
  );
};

const RequiredAttachmentsInternationalDocumentsGrid = ({ loading, disabled }) => {
  const { reportWarnings } = useAttachmentValidation();
  const isInternationalDocsEnabled = useVeroFlag(
    VeroFlag.useInternationalDocumentUpload
  );
  const isRunwayEnabled = useVeroFlag(VeroFlag.useDocumentRunwayCalculation);
  const {
    values,
    onChangeAttachment,
    onChangeQuantity,
    onChangeRunway,
    handleAddCustomDoc,
    handleDeleteDoc,
  } = useAttachmentHandlers({
    attachmentType: ATTACHMENT_TYPE,
    personas: APPLICANT_TYPES_CONFIG,
  });
  const onGuardedTogglePersona = useGuardedIncomeDocsToggle({
    surfaceFamily: "international",
  });

  if (!isInternationalDocsEnabled) return null;

  const allItems = APPLICANT_TYPES_CONFIG.flatMap((at) =>
    mapIntlDocLabels(getRequiredAttachments(values, at.applicantType))
  );

  return (
    <div id="international-grid">
      <Settings
        header={
          <Skeleton loading={loading}>
            <ApplicationSetupHeader
              title="International Documents Setup"
              subTitle="Manage the document types available for applicants with international income. Hover over the info icon next to a document type to learn more."
            />
            <SupportedCountriesSection />
          </Skeleton>
        }
        nodes={[
          {
            key: 1,
            component: (
              <Skeleton loading={loading}>
                <IncomeSurfaceGate>
                  <ConsolidatedAttachmentGrid
                    personas={APPLICANT_TYPES_CONFIG}
                    items={allItems}
                    values={values}
                    onTogglePersona={onGuardedTogglePersona}
                    onToggleItem={onChangeAttachment}
                    onChangeQuantity={onChangeQuantity}
                    onAddCustomDoc={handleAddCustomDoc}
                    onDeleteDoc={handleDeleteDoc}
                    buildLabel={buildItemDisplayLabel}
                    sectionTitle="International Documents"
                    sectionId="international-grid"
                    onValidationChange={(w) => reportWarnings("international", w)}
                    disabled={disabled}
                    onChangeRunway={onChangeRunway}
                    isRunwayEnabled={isRunwayEnabled}
                  />
                </IncomeSurfaceGate>
              </Skeleton>
            ),
          },
        ]}
      />
    </div>
  );
};

RequiredAttachmentsInternationalDocuments.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachmentsInternationalDocuments.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachmentsInternationalDocuments;
